import type { LookupFieldConfig } from './MasterDocument.types';

// ════════════════════════════════════════════════════════════
// CASCADING LOOKUPS — runtime para filterDependencies
// ════════════════════════════════════════════════════════════

/** Un valor de filtro vacío (null/undefined/"") no debe enviarse al backend. */
export function isFilterValueEmpty(v: unknown): boolean {
  return v === null || v === undefined || v === '';
}

export interface DependentLookupProps {
  /** getFilters dinámico: combina filters estáticos + dinámicos por padre. */
  getFilters: () => Record<string, unknown>;
  /** Valores actuales de los campos padre (disparan recarga al cambiar). */
  dependencies: unknown[];
  /** true si falta el valor de algún padre → el searcher debe deshabilitarse. */
  parentMissing: boolean;
}

/**
 * Construye los props `getFilters`/`dependencies` para un LookupField cuyo
 * config declara `filterDependencies` (cascading lookups). Devuelve `undefined`
 * cuando el config no tiene `filterDependencies` (comportamiento legacy intacto).
 *
 * Semántica de filterDependencies: clave = campo de filtro que espera el backend;
 * valor = key del campo del formulario cuyo valor se usa como filtro.
 *
 * @param config   Config del LookupField (puede incluir `filters` estáticos).
 * @param resolve  Resuelve la key de un campo del formulario a su valor actual.
 */
export function buildDependentLookupProps(
  config: LookupFieldConfig | undefined,
  resolve: (formKey: string) => unknown,
): DependentLookupProps | undefined {
  const deps = config?.filterDependencies;
  if (!config || !deps) return undefined;
  const entries = Object.entries(deps); // [backendField, formKey]
  if (entries.length === 0) return undefined;

  const parentValues = entries.map(([, formKey]) => resolve(formKey));
  const parentMissing = parentValues.some(isFilterValueEmpty);

  // Sólo se mergea la forma de objeto simple; la forma de array (FilterExpression)
  // no se combina con el objeto dinámico.
  const staticFilters =
    config.filters && !Array.isArray(config.filters)
      ? (config.filters as Record<string, unknown>)
      : undefined;

  const getFilters = () => {
    const dynamic: Record<string, unknown> = {};
    entries.forEach(([backendField, formKey]) => {
      const v = resolve(formKey);
      // OMITE entradas vacías: nunca mandar `{ campo: undefined }`.
      if (!isFilterValueEmpty(v)) dynamic[backendField] = v;
    });
    return { ...(staticFilters ?? {}), ...dynamic };
  };

  return { getFilters, dependencies: parentValues, parentMissing };
}

/**
 * Mapea, en reversa, key de campo padre → keys de searchers dependientes, a
 * partir de un conjunto de LookupFieldConfig. Se usa para limpiar el valor del
 * dependiente cuando cambia un padre (p.ej. al cambiar bodega, resetear ubicación).
 *
 * @param lookupConfigs  Mapa de configs por key de campo.
 * @param onlyKeys       Si se provee, sólo considera dependientes en este set
 *                       (p.ej. los campos presentes en el form de línea).
 */
export function buildDependentsByParent(
  lookupConfigs: Record<string, LookupFieldConfig> | undefined,
  onlyKeys?: Set<string>,
): Record<string, string[]> {
  const map: Record<string, string[]> = {};
  if (!lookupConfigs) return map;
  for (const [depKey, cfg] of Object.entries(lookupConfigs)) {
    if (onlyKeys && !onlyKeys.has(depKey)) continue;
    const deps = cfg.filterDependencies;
    if (!deps) continue;
    for (const parentKey of Object.values(deps)) {
      if (parentKey === depKey) continue; // un campo no se depende de sí mismo
      (map[parentKey] ??= []).push(depKey);
    }
  }
  return map;
}
